/**
 * Create Skill Template - Platform admin form for creating new skill templates
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPageLayout, Button, FormInput, FormTextArea, FormSelect, Section, Card, Grid, Col, Badge } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';
import './SkillTemplateForm.css';

const CreateSkillTemplate = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const isClubSkill = window.location.pathname.includes('/admin/skills/') || window.location.pathname.includes('/coach/skills/');
  const backPath = isClubSkill ? '/admin/skills' : '/admin/skill-templates';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    difficulty_level: '',
    instruction_text: '',
    instruction_link: '',
    image_url: '',
    video_url: '',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const SKILL_CATEGORIES = [
    { value: 'passing', label: 'Passing' },
    { value: 'shooting', label: 'Shooting' },
    { value: 'dribbling', label: 'Dribbling' },
    { value: 'defending', label: 'Defending' },
    { value: 'heading', label: 'Heading' },
    { value: 'ball_control', label: 'Ball Control' },
    { value: 'positioning', label: 'Positioning' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'mental', label: 'Mental' }
  ];

  const DIFFICULTY_LEVELS = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Please provide a skill name');
      return;
    }

    if (!formData.category) {
      showError('Please select a category');
      return;
    }

    if (!formData.difficulty_level) {
      showError('Please select a difficulty level');
      return;
    }

    setIsSubmitting(true);

    try {
      // Don't send empty strings for category and difficulty_level
      const payload = {
        ...formData,
        category: formData.category || undefined,
        difficulty_level: formData.difficulty_level || undefined
      };

      if (isClubSkill) {
        await apiService.request('/api/skills/club-skills', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        showSuccess('Club skill created successfully');
      } else {
        await apiService.createSkillTemplate(payload);
        showSuccess('Skill template created successfully');
      }
      navigate(backPath);
    } catch (err) {
      showError(`Failed to create: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminPageLayout
      title="CREATE SKILL"
      subtitle="Create a new skill for player development"
      backPath={backPath}
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

          <Section title="Basic Information">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormInput
                  label="Skill Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Passing Accuracy"
                  required
                />

                <FormTextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe what this skill is about..."
                  rows={4}
                />

                <Grid columns={2} gap="1.5rem">
                  <Col span={1}>
                    <FormSelect
                      label="Category *"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      placeholder="Select Category"
                      options={SKILL_CATEGORIES}
                    />
                  </Col>
                  <Col span={1}>
                    <FormSelect
                      label="Difficulty Level *"
                      name="difficulty_level"
                      value={formData.difficulty_level}
                      onChange={handleChange}
                      required
                      placeholder="Select Level"
                      options={DIFFICULTY_LEVELS}
                    />
                  </Col>
                </Grid>
              </div>
            </Card>
          </Section>

          <Section title="Instructions">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormTextArea
                  label="How to Practice"
                  name="instruction_text"
                  value={formData.instruction_text}
                  onChange={handleChange}
                  placeholder="Explain how to practice this skill..."
                  rows={4}
                />

                <FormInput
                  label="Resource Link"
                  name="instruction_link"
                  value={formData.instruction_link}
                  onChange={handleChange}
                  type="url"
                  placeholder="https://example.com/passing-drills"
                />
              </div>
            </Card>
          </Section>

          <Section title="Media">
            <Card level={1}>
              <Grid columns={2} gap="1.5rem">
                <Col span={1}>
                  <FormInput
                    label="Image URL"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    type="url"
                    placeholder="https://example.com/image.jpg"
                  />
                </Col>
                <Col span={1}>
                  <FormInput
                    label="Video URL"
                    name="video_url"
                    value={formData.video_url}
                    onChange={handleChange}
                    type="url"
                    placeholder="https://example.com/video.mp4"
                  />
                </Col>
              </Grid>
            </Card>
          </Section>

          <Section title="Status">
            <Card level={1}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'var(--surface-lvl2)',
                  borderRadius: '12px',
                  border: '1px solid var(--color-border-light)',
                  flex: 1
                }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label htmlFor="is_active" style={{ cursor: 'pointer', fontWeight: 600 }}>
                    Active
                  </label>
                </div>
                {formData.is_active ? (
                  <Badge variant="success" size="sm">ACTIVE</Badge>
                ) : (
                  <Badge variant="warning" size="sm">INACTIVE</Badge>
                )}
              </div>
            </Card>
          </Section>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '1rem',
            padding: '2rem',
            background: 'var(--surface-lvl1)',
            borderRadius: '16px',
            border: '1px solid var(--color-border-light)'
          }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/skill-templates')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="save"
              loading={isSubmitting}
              disabled={!formData.name || !formData.category || !formData.difficulty_level}
            >
              Create Skill
            </Button>
          </div>
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default CreateSkillTemplate;
